import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, CreditCard, Building2, Wallet, DollarSign, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@vaiu/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner } from '@vaiu/ui';
import { ErrorMessage } from '@vaiu/ui';
import { StatusBadge } from '@vaiu/ui';
// Removed ConfirmDialog import - using inline confirmation instead
import { platformApi, PaymentMethod } from '@/services/platformApi';
import PaymentMethodCardRefactored from '@/components/payment/PaymentMethodCardRefactored';
import { i18n } from '@/utils/i18n';
import TopUpBalance from '@/components/TopUpBalance';
import { roundMonetaryValue, formatMonetaryValue } from '@vaiu/utils/currency';

const PaymentSourcesPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [methodToDelete, setMethodToDelete] = useState<PaymentMethod | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Top-up functionality state
  const [showTopUpSection, setShowTopUpSection] = useState(false);
  const [balances, setBalances] = useState<{ [key: string]: number }>({});
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
  const [customTopUpAmount, setCustomTopUpAmount] = useState<number>(10);
  const [showTopUpDialog, setShowTopUpDialog] = useState(false);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      const methods = await platformApi.getPaymentMethods();
      
      // Fetch Stripe bank accounts (when use_stripe_bank is enabled) and merge into list
      try {
        const stripeBanks = await platformApi.getStripeBankAccounts();
        methods.push(...stripeBanks);
      } catch (stripeErr) {
        // Stripe bank may be disabled or endpoint unavailable - ignore
      }
      
      // Always add PayPal as a funding option (no user email required for funding)
      methods.push({
        id: 'paypal-funding',
        type: 'paypal',
        details: {},
        isDefault: false,
        createdAt: new Date().toISOString()
      });
      
      setPaymentMethods(methods);
    } catch (err: any) {
      console.error('Error fetching payment methods:', err);
      
      // Handle different types of error objects
      let errorMessage = 'Failed to load payment methods';
      
      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error) {
        errorMessage = err.error;
      } else if (err?.statusText) {
        errorMessage = err.statusText;
      } else if (typeof err === 'object') {
        // If it's an object, try to extract meaningful info
        errorMessage = JSON.stringify(err) !== '{}' 
          ? `Error: ${JSON.stringify(err)}`
          : 'An unknown error occurred';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalances = async () => {
    try {
      const userBalances = await platformApi.getBalances();
      setBalances(userBalances);
      
      // Set default currency based on user's primary currency
      if (user?.currency) {
        setSelectedCurrency(user.currency);
      }
    } catch (err: any) {
      console.error('Error fetching balances:', err);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
    fetchBalances();
  }, []);

  const handleAddMethod = () => {
    navigate('/payment-methods/add');
  };

  const handleSetDefault = async (methodId: string) => {
    try {
      await platformApi.setDefaultPaymentMethod(methodId);
      // Update local state
      setPaymentMethods(prev => 
        prev.map(method => ({
          ...method,
          isDefault: method.id === methodId
        }))
      );
      toast({
        title: 'Default payment method updated',
        description: 'Your default payment method has been changed.',
      });
    } catch (err: any) {
      toast({
        title: 'Error updating default payment method',
        description: err.message || 'Failed to update default payment method',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteMethod = async () => {
    if (!methodToDelete) return;
    
    try {
      setDeleting(true);
      await platformApi.removePaymentMethod(methodToDelete.id);
      setPaymentMethods(prev => prev.filter(method => method.id !== methodToDelete.id));
      setMethodToDelete(null);
      toast({
        title: 'Payment method removed',
        description: 'Your payment method has been successfully removed.',
      });
    } catch (err: any) {
      toast({
        title: 'Error removing payment method',
        description: err.message || 'Failed to remove payment method',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleQuickTopUp = (amount: number) => {
    setCustomTopUpAmount(amount);
    setShowTopUpDialog(true);
  };

  const handleCustomTopUp = () => {
    if (customTopUpAmount > 0) {
      setShowTopUpDialog(true);
    }
  };

  const handleTopUpSuccess = (newBalance: number) => {
    setShowTopUpDialog(false);
    // Update local balance state
    setBalances(prev => ({
      ...prev,
      [selectedCurrency]: newBalance
    }));
    toast({
      title: 'Balance Updated',
      description: `Your ${selectedCurrency} balance has been topped up successfully.`,
    });
  };

  const handleTopUpCancel = () => {
    setShowTopUpDialog(false);
  };

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'stripe':
        return <CreditCard className="w-5 h-5" />;
      case 'plaid':
        return <Building2 className="w-5 h-5" />;
      case 'paypal':
        return <Wallet className="w-5 h-5" />;
      default:
        return <CreditCard className="w-5 h-5" />;
    }
  };

  const topUpMethods = paymentMethods.filter(m => m.type === 'stripe' || m.type === 'plaid' || m.type === 'paypal');
  const currentBalance = balances[selectedCurrency] || 0;

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center space-x-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="p-2"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{i18n.__('vaiu.payment.funding_sources')}</h1>
              <p className="text-sm text-muted-foreground">{i18n.__('vaiu.payment.manage_funding_sources')}</p>
            </div>
          </div>
          <Button 
            onClick={handleAddMethod}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{i18n.__('paymentSources.manageAccounts')}</span>
          </Button>
        </div>

        {/* Balance Top-Up Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5" />
                <span>{i18n.__('paymentSources.balanceTopUp')}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTopUpSection(!showTopUpSection)}
              >
                {showTopUpSection ? i18n.__('paymentSources.hide') : i18n.__('paymentSources.show')}
              </Button>
            </CardTitle>
            <CardDescription>
              {i18n.__('paymentSources.addFunds')}
            </CardDescription>
          </CardHeader>
          {showTopUpSection && (
            <CardContent className="space-y-4">
              {/* Balance Display */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">{i18n.__('paymentSources.currentBalance')}</p>
                    <div className="flex items-center space-x-2">
                      <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                        <SelectTrigger className="w-20 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(balances).map(currency => (
                            <SelectItem key={currency} value={currency}>
                              {currency}
                            </SelectItem>
                          ))}
                          {!Object.keys(balances).includes('USD') && (
                            <SelectItem value="USD">USD</SelectItem>
                          )}
                          {!Object.keys(balances).includes('EUR') && (
                            <SelectItem value="EUR">EUR</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <span className="text-2xl font-bold text-blue-800">
                        {formatMonetaryValue(roundMonetaryValue(currentBalance, 2), 2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Show inline TopUpBalance component when dialog is "open" */}
              {showTopUpDialog && (
                <TopUpBalance
                  isOpen={showTopUpDialog}
                  currentBalance={currentBalance}
                  requiredAmount={currentBalance + customTopUpAmount}
                  currency={selectedCurrency}
                  onSuccess={handleTopUpSuccess}
                  onCancel={handleTopUpCancel}
                />
              )}

              {/* Only show quick top-up controls when not in top-up mode */}
              {!showTopUpDialog && topUpMethods.length > 0 ? (
                <>
                  <div>
                    <Label className="text-sm font-medium">{i18n.__('paymentSources.quickTopUp')}</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {[10, 25, 50].map(amount => (
                        <Button
                          key={amount}
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuickTopUp(amount)}
                          className="flex flex-col py-3 h-auto"
                        >
                          <span className="text-lg font-bold">{selectedCurrency} {amount}</span>
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Amount */}
                  <div className="space-y-2">
                    <Label htmlFor="custom-amount">{i18n.__('paymentSources.customAmount')}</Label>
                    <div className="flex space-x-2">
                      <Input
                        id="custom-amount"
                        type="number"
                        step="0.01"
                        min="1"
                        value={customTopUpAmount}
                        onChange={(e) => setCustomTopUpAmount(parseFloat(e.target.value) || 0)}
                        placeholder={i18n.__('paymentSources.enterAmount')}
                        className="flex-1"
                      />
                      <Button 
                        onClick={handleCustomTopUp}
                        disabled={customTopUpAmount <= 0}
                      >
                        {i18n.__('paymentSources.topUp')}
                      </Button>
                    </div>
                  </div>

                  {/* Available Payment Methods Summary */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600 mb-2">
                      {i18n.__(topUpMethods.length === 1 ? 'paymentSources.availableMethods' : 'paymentSources.availableMethods_plural', { count: topUpMethods.length })}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {topUpMethods.slice(0, 3).map((method) => (
                        <span key={method.id} className="text-xs bg-white px-2 py-1 rounded border">
                          {method.type === 'stripe' ? `Card •••• ${method.details.last4}` : 
                           method.type === 'plaid' ? `${method.details.accountName}` : 
                           method.type === 'paypal' ? 'PayPal' : method.type}
                        </span>
                      ))}
                      {topUpMethods.length > 3 && (
                        <span className="text-xs text-gray-500">+{topUpMethods.length - 3} {i18n.__('paymentSources.more')}</span>
                      )}
                    </div>
                  </div>
                </>
              ) : !showTopUpDialog && (
                <div className="text-center py-4 text-gray-500">
                  <p>{i18n.__('paymentSources.addMethodPrompt')}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddMethod}
                    className="mt-2"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {i18n.__('paymentSources.addMethod')}
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Payment Methods List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Wallet className="w-5 h-5" />
              <span>{i18n.__('paymentSources.yourMethods')}</span>
            </CardTitle>
            <CardDescription>
              {i18n.__('paymentSources.yourMethodsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <LoadingSpinner text={i18n.__('paymentSources.loading')} />
            ) : error ? (
              <ErrorMessage
                message={error}
                onRetry={fetchPaymentMethods}
              />
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-8">
                <Wallet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">{i18n.__('paymentSources.noMethods')}</h3>
                <p className="text-muted-foreground mb-4">
                  {i18n.__('paymentSources.noMethodsDesc')}
                </p>
                <Button onClick={handleAddMethod}>
                  <Plus className="w-4 h-4 mr-2" />
                  {i18n.__('paymentSources.addFirstAccount')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {paymentMethods.map((method) => (
                  <PaymentMethodCardRefactored
                    key={method.id}
                    method={method}
                    onSetDefault={handleSetDefault}
                    onDelete={(method) => setMethodToDelete(method)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inline Delete Confirmation */}
        {methodToDelete && (
          <Card className="mt-6 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-900 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                {i18n.__('paymentSources.removeTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>{i18n.__('paymentSources.removeConfirm')}</p>
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {i18n.__('paymentSources.removeWarning')}
                </AlertDescription>
              </Alert>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setMethodToDelete(null)}
                  disabled={deleting}
                  className="flex-1"
                >
                  {i18n.__('common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteMethod}
                  disabled={deleting}
                  className="flex-1"
                >
                  {deleting ? i18n.__('paymentSources.removing') : i18n.__('paymentSources.remove')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PaymentSourcesPage; 